<template>
  <div class="field-mapping-editor">
    <div class="header mb-4">
      <h3 class="text-lg font-semibold">Field Mappings</h3>
      <p class="text-sm text-gray-600">
        Map ERP/Application fields (left) to Salesforce fields (right)
      </p>
    </div>

    <!-- Object Type Selector -->
    <div class="mb-4">
      <label class="block text-sm font-medium mb-2">Salesforce Object Type</label>
      <div class="flex space-x-4">
        <label class="flex items-center">
          <input 
            type="radio" 
            value="Lead" 
            v-model="targetObject"
            class="mr-2"
          >
          Lead
        </label>
        <label class="flex items-center">
          <input 
            type="radio" 
            value="Contact" 
            v-model="targetObject"
            class="mr-2"
          >
          Contact
        </label>
      </div>
    </div>

    <!-- Mapping List -->
    <div class="mappings-container">
      <draggable 
        v-model="mappings"
        item-key="id"
        handle=".drag-handle"
        class="space-y-2"
      >
        <template #item="{ element: mapping, index }">
          <div class="mapping-row bg-white border rounded-lg p-4 flex items-center space-x-4">
            <!-- Drag Handle -->
            <div class="drag-handle cursor-move text-gray-400">
              <i class="fas fa-grip-vertical"></i>
            </div>

            <!-- Mapping Type -->
            <select 
              v-model="mapping.mapping_type"
              class="form-select w-32"
              @change="onMappingTypeChange(mapping)"
            >
              <option value="direct">Direct</option>
              <option value="hardcoded">Hardcoded</option>
              <option value="computed">Computed</option>
            </select>

            <!-- Source Field (for direct mapping) -->
            <div v-if="mapping.mapping_type === 'direct'" class="flex-1">
              <label class="block text-xs font-medium text-gray-500 mb-1">ERP/App Field</label>
              <select 
                v-model="mapping.source_field"
                class="form-select w-full"
              >
                <option value="">Select ERP field...</option>
                <optgroup label="Campaign Fields">
                  <option value="name">Campaign Name</option>
                  <option value="goal">Goal Amount</option>
                  <option value="event_date">Event Date</option>
                  <option value="business_name">Business Name</option>
                </optgroup>
                <optgroup label="User Fields">
                  <option value="user.first_name">User First Name</option>
                  <option value="user.last_name">User Last Name</option>
                  <option value="user.email">User Email</option>
                  <option value="user.address">User Address</option>
                  <option value="user.city">User City</option>
                  <option value="user.state">User State</option>
                  <option value="user.zip">User Zip</option>
                </optgroup>
              </select>
            </div>

            <!-- Mapping Value (for hardcoded) -->
            <div v-else-if="mapping.mapping_type === 'hardcoded'" class="flex-1">
              <label class="block text-xs font-medium text-gray-500 mb-1">Static Value</label>
              <input 
                v-model="mapping.mapping_value"
                type="text"
                class="form-input w-full"
                placeholder="Enter value..."
              >
            </div>

            <!-- Computed Type (for computed) -->
            <div v-else-if="mapping.mapping_type === 'computed'" class="flex-1">
              <label class="block text-xs font-medium text-gray-500 mb-1">Computed Value</label>
              <select 
                v-model="mapping.mapping_value"
                class="form-select w-full"
              >
                <option value="">Select computation...</option>
                <option value="campaign_url">Campaign URL</option>
                <option value="foundation_name">Foundation Name</option>
                <option value="parent_campaign_name">Parent Campaign Name</option>
                <option value="full_address">Full Address</option>
              </select>
            </div>

            <!-- Arrow -->
            <div class="text-gray-400">
              <i class="fas fa-arrow-right"></i>
            </div>

            <!-- Target Field -->
            <div class="w-64">
              <label class="block text-xs font-medium text-gray-500 mb-1">Salesforce Field</label>
              <select 
                v-model="mapping.target_field"
                class="form-select w-full"
              >
                <option value="">Select Salesforce field...</option>
                <option 
                  v-for="(label, field) in getSalesforceFields()" 
                  :key="field"
                  :value="field"
                >
                  {{ label }}
                </option>
              </select>
            </div>

            <!-- Delete Button -->
            <button 
              @click="removeMapping(index)"
              class="text-red-500 hover:text-red-700"
            >
              <i class="fas fa-trash"></i>
            </button>
          </div>
        </template>
      </draggable>
    </div>

    <!-- Add Mapping Button -->
    <div class="mt-4">
      <button 
        @click="addMapping"
        class="btn btn-secondary"
      >
        <i class="fas fa-plus mr-2"></i>
        Add Mapping
      </button>
      
      <button 
        v-if="templates.length > 0"
        @click="showTemplateSelector = true"
        class="btn btn-outline ml-2"
      >
        <i class="fas fa-file-import mr-2"></i>
        Apply Template
      </button>
    </div>

    <!-- Template Selector Modal -->
    <div 
      v-if="showTemplateSelector"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="showTemplateSelector = false"
    >
      <div class="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 class="text-lg font-semibold mb-4">Select Template</h3>
        <select 
          v-model="selectedTemplate"
          class="form-select w-full mb-4"
        >
          <option value="">Select a template...</option>
          <option 
            v-for="template in templates" 
            :key="template.id"
            :value="template.id"
          >
            {{ template.name }}
          </option>
        </select>
        <div class="flex justify-end space-x-2">
          <button 
            @click="showTemplateSelector = false"
            class="btn btn-secondary"
          >
            Cancel
          </button>
          <button 
            @click="applyTemplate"
            :disabled="!selectedTemplate"
            class="btn btn-primary"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onUnmounted } from 'vue';
import draggable from 'vuedraggable';

export default {
  name: 'FieldMappingEditor',
  components: {
    draggable,
  },
  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
    templates: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const mappings = ref(props.modelValue || []);
    const targetObject = ref('Lead');
    const showTemplateSelector = ref(false);
    const selectedTemplate = ref('');
    
    // Cache for preserving field mappings when switching object types
    const mappingCache = ref({
      Lead: [],
      Contact: []
    });

    // Salesforce field schemas
    const leadFields = {
      'LastName': 'Last Name',
      'FirstName': 'First Name',
      'Company': 'Company',
      'Street': 'Street',
      'City': 'City',
      'State': 'State/Province',
      'PostalCode': 'Postal Code',
      'Email': 'Email',
      'Phone': 'Phone',
      'LeadSource': 'Lead Source',
      'Rating': 'Rating',
      'Status': 'Status',
      'Industry': 'Industry',
      'Website': 'Website',
      'Description': 'Description',
      'NumberOfEmployees': 'Number of Employees',
      'AnnualRevenue': 'Annual Revenue',
      // Custom fields
      'Goal_Amount__c': 'Goal Amount',
      'Event_Date__c': 'Event Date',
      'Campaign__c': 'Campaign',
      'Fundraising_Page__c': 'Fundraising Page',
      'Campaign_Type__c': 'Campaign Type',
      'Side_Out_Program_Name__c': 'Side-Out Program Name',
      'Pipeline_Status__c': 'Pipeline Status',
    };

    const contactFields = {
      'LastName': 'Last Name',
      'FirstName': 'First Name',
      'Email': 'Email',
      'Phone': 'Phone',
      'MailingStreet': 'Mailing Street',
      'MailingCity': 'Mailing City',
      'MailingState': 'Mailing State/Province',
      'MailingPostalCode': 'Mailing Postal Code',
      'MailingCountry': 'Mailing Country',
      'OtherStreet': 'Other Street',
      'OtherCity': 'Other City',
      'OtherState': 'Other State/Province',
      'OtherPostalCode': 'Other Postal Code',
      'OtherCountry': 'Other Country',
      'Department': 'Department',
      'Title': 'Title',
      'Birthdate': 'Birthdate',
      'Description': 'Description',
      // Custom fields
      'Campaign_Type__c': 'Campaign Type',
      'Dig_Pink_Campaign__c': 'Dig Pink Campaign',
      'Dig_Pink_Campaign_Name__c': 'Dig Pink Campaign Name',
      'Dig_Pink_Parent_Campaign_Name__c': 'Dig Pink Parent Campaign Name',
      'Fundraiser_Page__c': 'Fundraiser Page',
    };

    const getSalesforceFields = () => {
      return targetObject.value === 'Lead' ? leadFields : contactFields;
    };

    // Helper function to find similar fields between Lead and Contact
    const findSimilarField = (fieldName, targetFields) => {
      // Map common field transformations between Lead and Contact
      const fieldMappings = {
        'Street': 'MailingStreet',
        'City': 'MailingCity', 
        'State': 'MailingState',
        'PostalCode': 'MailingPostalCode',
        'MailingStreet': 'Street',
        'MailingCity': 'City',
        'MailingState': 'State',
        'MailingPostalCode': 'PostalCode'
      };
      
      // Check for direct mapping
      if (fieldMappings[fieldName] && targetFields[fieldMappings[fieldName]]) {
        return fieldMappings[fieldName];
      }
      
      // Check if same field exists
      if (targetFields[fieldName]) {
        return fieldName;
      }
      
      return null;
    };

    const addMapping = () => {
      mappings.value.push({
        id: Date.now() + Math.random(),
        mapping_type: 'direct',
        source_field: '',
        mapping_value: '',
        target_object: targetObject.value,
        target_field: '',
      });
    };

    const removeMapping = (index) => {
      mappings.value.splice(index, 1);
    };

    const onMappingTypeChange = (mapping) => {
      // Clear irrelevant fields when type changes
      if (mapping.mapping_type === 'direct') {
        mapping.mapping_value = '';
      } else if (mapping.mapping_type === 'hardcoded' || mapping.mapping_type === 'computed') {
        mapping.source_field = '';
      }
    };

    const applyTemplate = () => {
      const template = props.templates.find(t => t.id === selectedTemplate.value);
      if (template && template.mappings) {
        mappings.value = JSON.parse(template.mappings);
      }
      showTemplateSelector.value = false;
      selectedTemplate.value = '';
    };

    // Watch for changes in target object
    watch(targetObject, (newValue, oldValue) => {
      // Save current mappings to cache for the old object type
      if (oldValue && mappings.value.length > 0) {
        mappingCache.value[oldValue] = JSON.parse(JSON.stringify(mappings.value));
      }
      
      // Check if we have cached mappings for the new object type
      if (mappingCache.value[newValue] && mappingCache.value[newValue].length > 0) {
        // Restore cached mappings
        mappings.value = JSON.parse(JSON.stringify(mappingCache.value[newValue]));
      } else {
        // Update existing mappings to use the new target object
        mappings.value.forEach(mapping => {
          mapping.target_object = newValue;
          // Only clear if field truly doesn't exist in new object
          const fields = newValue === 'Lead' ? leadFields : contactFields;
          if (!fields[mapping.target_field]) {
            // Try to find a similar field (e.g., Street -> MailingStreet)
            const similarField = findSimilarField(mapping.target_field, fields);
            mapping.target_field = similarField || '';
          }
        });
      }
    });

    // Watch mappings and emit changes
    watch(mappings, (newValue) => {
      emit('update:modelValue', newValue);
    }, { deep: true });

    // Watch for prop changes
    watch(() => props.modelValue, (newValue) => {
      mappings.value = newValue || [];
      // Determine target object from existing mappings
      if (mappings.value.length > 0) {
        targetObject.value = mappings.value[0].target_object || 'Lead';
      }
    }, { deep: true });

    // Clear cache when component unmounts
    onUnmounted(() => {
      mappingCache.value = { Lead: [], Contact: [] };
    });

    return {
      mappings,
      targetObject,
      showTemplateSelector,
      selectedTemplate,
      getSalesforceFields,
      addMapping,
      removeMapping,
      onMappingTypeChange,
      applyTemplate,
      findSimilarField,
      mappingCache,
    };
  },
};
</script>

<style scoped>
.form-select,
.form-input {
  @apply border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500;
}

.btn {
  @apply px-4 py-2 rounded font-medium transition-colors;
}

.btn-primary {
  @apply bg-blue-500 text-white hover:bg-blue-600;
}

.btn-secondary {
  @apply bg-gray-500 text-white hover:bg-gray-600;
}

.btn-outline {
  @apply border border-gray-300 text-gray-700 hover:bg-gray-50;
}

.btn:disabled {
  @apply opacity-50 cursor-not-allowed;
}
</style>